import React from 'react';

class DependenciesContainer {
    static NONE = Symbol('none');

    constructor(parent) {
        this.map = new Map();
        this.parent = parent;
    }

    lookup(key) {
        // standard usage has single root container
        if (this.map.has(key)) {
            return this.map.get(key);
        }
        // mocks might have nested providers
        // each overriding the resolved dependency of its parent provider
        let { parent } = this;
        while (parent != null) {
            if (parent.map.has(key)) {
                return parent.map.get(key);
            }
            // eslint-disable-next-line
            parent = parent.parent;
        }
        return DependenciesContainer.NONE;
    }

    set(dependency, value) {
        return this.map.set(dependency, value);
    }
}

const rootContainer = new DependenciesContainer(null);

const { Consumer, Provider } = React.createContext(rootContainer);

const resolve = (dependency, container) => {
    let resolved = container.lookup(dependency);
    if (resolved === DependenciesContainer.NONE) {
        resolved = dependency();
        // in case dependency is not found in current container and above
        // adding it to the very root to be shared among every component in the tree
        // that might request the dependency at later point in time
        /**
         *         <RootProvider>
         *               |
         *      ---------------------
         *     |                     |
         *  <Mock a >                |
         *     |                  <Resolver b c >
         *     |
         * <Resolver a b >
         *
         * dependency `b` should be stored in `RootProvider` container and be shared
         * despite the fact that <Mock a > created a new `Provider` scope.
         */
        rootContainer.set(dependency, resolved);
    }
    return resolved;
};

// children is a reserved prop for every component
const PROP_CHILDREN = 'children';

const resolveProps = (props, container) => {
    const resolved = Object.create(null);
    // eslint-disable-next-line
    for (const propName in props) {
        if (propName !== PROP_CHILDREN) {
            resolved[propName] = resolve(props[propName], container);
        }
    }
    return resolved;
};

/**
 * <Resolver> component
 * Resolves dependencies described in props with provided resolvers
 * @example:
 * <Resolver apollo={apolloResolver} fetch={fetchResolver}>
 *     {({ apollo, fetch }) => (...)
 * </Resolver>
 */
const Resolver = props => (
    <Consumer>{container => props.children(resolveProps(props, container))}</Consumer>
);

/**
 * withDependencies Higher Order Component
 * @example:
 * const MyComponentWithDependencies = withDependencies({ cookie: cookieResolver })(MyComponent);
 */
const withDependencies = dependencies => Component => props => (
    <Resolver {...dependencies}>
        {resolved => (
            <Component
                {...resolved}
                {...props}
            />
        )}
    </Resolver>
);

/**
 * <Mock> component
 * Mocks dependencies of the underlying <Resolver> components.
 *
 * @example:
 * <Mock cookie={{ resolver: cookieResolver, value: mockedCookie }}>
 *      <div>...</div>
 * </Mock>
 */
const Mock = props => (
    <Consumer>
        {parent => {
            const container = new DependenciesContainer(parent);
            // eslint-disable-next-line
            for (const propName in props) {
                if (propName !== PROP_CHILDREN) {
                    const { resolver, value } = props[propName];
                    container.set(resolver, value);
                }
            }
            return (
                <Provider
                    children={props.children}
                    value={container}
                />
            );
        }}
    </Consumer>
);

export {
    withDependencies,
    Resolver,
    Mock,
};
